// ProveedorAuthContext — contexto de autenticación para proveedores del
// marketplace (Sprint 14, Bloque C).
//
// Difiere del ClienteAuthContext: usa Supabase Auth nativo (no JWT custom)
// porque eso ahorra un endpoint custom de auth (estamos en 12/12 functions
// del plan Vercel Hobby). El magic link lo manda Supabase directo desde su
// SMTP propio al email del proveedor.
//
// Difiere del AuthContext (ejecutivos) en cuanto a UX: aquí no hay
// password — todo es magic link + Supabase Auth session. El AuthContext
// ya maneja Supabase Auth para ejecutivos; este context lo wrappea
// específicamente para proveedores con el join `auth.uid() → proveedores
// .user_id` para resolver "qué proveedor soy".

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const ProveedorAuthContext = createContext(null);

export function ProveedorAuthProvider({ children }) {
  const [session, setSession]     = useState(null);
  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const cargarProveedor = useCallback(async (sess) => {
    if (!sess?.user?.id) {
      setProveedor(null);
      return;
    }
    try {
      // Sprint 14.9 fix: si un proveedor se postuló (sin auth) y luego hace
      // login por primera vez, su fila proveedores.user_id está null. El
      // RPC vincular_proveedor_a_user() hace el match por email y vincula.
      // Es idempotente — si ya está vinculado, devuelve ok=true sin tocar nada.
      try {
        await supabase.rpc('vincular_proveedor_a_user');
      } catch (_) { /* best-effort, no bloquear el login */ }

      const { data, error: err } = await supabase
        .from('proveedores')
        .select('*, especialidades:proveedor_especialidades(categoria)')
        .eq('user_id', sess.user.id)
        .maybeSingle();
      if (err) throw err;
      if (!data) {
        setProveedor(null);
        setError('No hay un proveedor asociado a este email. Si todavía no te postulaste, hazlo en ia-vera.com/proveedor/postular.');
        return;
      }
      if (!data.activo) {
        setProveedor(null);
        setError('Tu cuenta de proveedor está inactiva.');
        return;
      }
      if (!data.certificado) {
        setProveedor(data);
        setError(null);
        // Estado especial: logueado pero sin certificación todavía
        return;
      }
      setProveedor(data);
      setError(null);
    } catch (e) {
      setError(`Error cargando perfil: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session);
      await cargarProveedor(data.session);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      if (!mounted) return;
      setSession(sess);
      await cargarProveedor(sess);
    });
    return () => { mounted = false; sub?.subscription?.unsubscribe?.(); };
  }, [cargarProveedor]);

  const enviarMagicLink = useCallback(async (email) => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        emailRedirectTo: `${window.location.origin}/proveedor`,
      },
    });
    if (err) throw new Error(err.message);
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProveedor(null);
  }, []);

  return (
    <ProveedorAuthContext.Provider value={{
      session,
      proveedor,
      loading,
      error,
      isAuthenticated: !!session,
      tieneProveedor:  !!proveedor,
      esCertificado:   !!proveedor?.certificado,
      enviarMagicLink,
      logout,
      reload:          () => cargarProveedor(session),
    }}>
      {children}
    </ProveedorAuthContext.Provider>
  );
}

export function useProveedorAuth() {
  const ctx = useContext(ProveedorAuthContext);
  if (!ctx) throw new Error('useProveedorAuth fuera de ProveedorAuthProvider');
  return ctx;
}
